import { useRouter } from 'next/router';
import { useContext, useEffect, useMemo, useState } from 'react';
import { ContactForm, Drawer, Option } from 'ui/components';
import { DictionaryContext } from 'application/adapters/context/Dictionary';
import { LocaleContext } from 'application/adapters/context/LocaleContext';
import { useFormContext } from 'application/adapters/context/FormContext';
import { postContactFormToUrl } from 'application/adapters/cms/formAdapter';
import { useDictionaryLocale } from 'application/hooks/useDictionaryLocale';
import { useContactLinkQuery } from 'application/hooks/useContactLinkQuery';
import { getNonUtmParams } from 'helpers/urlParams';
import { loadContactFormDictionary } from './contactFormDictionary';

type ContactFormFeatureProps = Umbraco.ContactForm & {
	contactLink?: { contactLink: Umbraco.Link };
};

export const ContactFormFeature: React.FC<ContactFormFeatureProps> = ({ content, settings, accessToken, culture, host, contactLink }) => {
	const dictionary = useContext(DictionaryContext);
	const localeContext = useContext(LocaleContext);
	const formContext = useFormContext();
	const router = useRouter();
	const [isLoading, setIsLoading] = useState(false);
	const [formIsSent, setFormIsSent] = useState(false);
	const [formSubmitError, setFormSubmitError] = useState(false);
	const reCaptchaApiKey = '6LdnLqgUAAAAAOC1Gp8qi-cEl-inU-ZKO_XwFRbZ';
	const [reCaptchaLoaded, setReCaptchaLoaded] = useState(false);
	const useAlternativeCaptcha = localeContext.getAlternativeCaptcha?.() ?? false;
	const currentLocale = useDictionaryLocale(culture);
	const contactFormDictionary = useMemo(() => loadContactFormDictionary(dictionary), [currentLocale]);
	const { validQueryParams, buildUrlWithParams } = useContactLinkQuery(contactLink);

	const transformedTopics = settings?.topics
		? Object.entries(settings.topics).map(([key, value], index) => ({
				children: value
					.replace(/\*/g, '')
					.trim()
					.replace(/([a-z])([A-Z])/g, '$1 $2'),
				categoryNumber: index + 1, // Start from 1 instead of 0
				id: key.replace(/\*/g, '').trim(),
		  }))
		: [];
	const transformedCountries = settings?.countries
		? Object.entries(settings.countries).map(([key, value]) => ({
				value: key.replace(/\*/g, '').trim(),
				text: value
					.replace(/\*/g, '')
					.trim()
					.replace(/([a-z])([A-Z])/g, '$1 $2'),
		  }))
		: [];

	const { globalContactLink, privacyPolicyLink, alternativeContactLink } = content?.properties || {};
	const transformedGlobalContactLink = globalContactLink
		? `<a href="${globalContactLink?.url}">${contactFormDictionary.formGlobalContactLinkText || globalContactLink?.name}</a>`
		: '';
	const transformedPrivacyPolicyLink = privacyPolicyLink
		? `<a href="${privacyPolicyLink?.url}">${contactFormDictionary.formPrivacyPolicyLinkText || privacyPolicyLink?.name}</a>`
		: '';
	const transformedAlternativeContactLink = alternativeContactLink
		? `<a href="${alternativeContactLink?.url}">${contactFormDictionary.formAlternativeContactLinkText || alternativeContactLink?.name}</a>`
		: '';

	const handleClose = () => {
		formContext.close();
		setFormIsSent(false);
		setFormSubmitError(false);

		// Remove contact query parameter from URL while preserving UTM params
		const currentQuery = window.location.search.substring(1);
		const searchParams = new URLSearchParams(currentQuery);
		const nonUtmQuery = new URLSearchParams(getNonUtmParams(searchParams)).toString();
		if (validQueryParams?.includes(nonUtmQuery)) {
			// Build URL without contact param but keep UTM params
			const queryString = buildUrlWithParams(false);
			const newUrl = `${window.location.pathname}${queryString ? '?' + queryString : ''}${window.location.hash}`;
			history.replaceState('', '', newUrl);
		}
	};

	const onSubmit = async (data) => {
		setIsLoading(true);
		setFormSubmitError(false);
		const ok = await postContactFormToUrl('Umbraco/Api/Forms/WebContact', data);
		// const ok = false; // For testing error state only

		if (ok) {
			setFormIsSent(true);
			setIsLoading(false);
		} else {
			setFormSubmitError(true);
			setIsLoading(false);
		}
	};

	useEffect(() => {
		const onQueryChange = (url: any) => {
			// For Next router events, the 'url' parameter will be a simple string
			let queryString: string;

			if (typeof url === 'object') {
				queryString = url.newURL ? new URL(url.newURL).search.substring(1) : window.location.search.substring(1);
			} else {
				queryString = url.includes('?') ? url.split('?')[1].split('#')[0] : '';
			}

			// Check if contact param exists by filtering out UTM params
			const searchParams = new URLSearchParams(queryString);
			const nonUtmParams = getNonUtmParams(searchParams);
			const nonUtmQuery = new URLSearchParams(nonUtmParams).toString();

			// Open form only if contact param is present (ignore UTM params)
			validQueryParams?.includes(nonUtmQuery) ? formContext.open() : formContext.close();
		};

		const onRouteChangeComplete = (url: string) => {
			// When route changes (including locale change), check if there's a query parameter
			if (url.includes('?')) {
				onQueryChange(url);
			} else {
				formContext.close();
			}
		};

		router.events.on('routeChangeComplete', onRouteChangeComplete);
		window.addEventListener('popstate', onQueryChange);

		// Trigger modal on initial load if URL contains valid query parameter
		const initialQuery = window.location.search.substring(1);
		if (initialQuery) {
			const initialSearchParams = new URLSearchParams(initialQuery);
			const initialNonUtmParams = getNonUtmParams(initialSearchParams);
			const initialNonUtmQuery = new URLSearchParams(initialNonUtmParams).toString();

			if (validQueryParams?.includes(initialNonUtmQuery)) {
				formContext.open();
			}
		}

		return () => {
			router.events.off('routeChangeComplete', onRouteChangeComplete);
			window.removeEventListener('popstate', onQueryChange);
		};
	}, [validQueryParams]);

	// Handle locale changes: clean up query parameter from URL only if form is closed
	useEffect(() => {
		// Skip on initial mount to avoid race condition with form opening
		const timeoutId = setTimeout(() => {
			const currentQuery = window.location.search.substring(1);
			const searchParams = new URLSearchParams(currentQuery);
			const nonUtmParams = getNonUtmParams(searchParams);
			const nonUtmQuery = new URLSearchParams(nonUtmParams).toString();

			if (!formContext.isOpen && nonUtmQuery && validQueryParams?.includes(nonUtmQuery)) {
				// Remove contact param but preserve UTM params
				const queryString = buildUrlWithParams(false);
				const newUrl = `${window.location.pathname}${queryString ? '?' + queryString : ''}${window.location.hash}`;
				router.push(newUrl, undefined, { shallow: true, scroll: false });
			}
		}, 200);

		return () => clearTimeout(timeoutId);
	}, [currentLocale, formContext.isOpen, validQueryParams, buildUrlWithParams, router]);

	return (
		<>
			{formContext.isOpen && (
				<Drawer handleClose={handleClose} isOpen={formContext.isOpen} side="right">
					<ContactForm
						dictionary={contactFormDictionary}
						{...content?.properties}
						onFormSubmit={onSubmit}
						isLoading={isLoading}
						formIsSent={formIsSent}
						formSubmitError={formSubmitError}
						topics={transformedTopics}
						countries={transformedCountries}
						reCaptchaApiKey={reCaptchaApiKey}
						onReCaptchaLoaded={() => setReCaptchaLoaded(true)}
						useAlternativeCaptcha={useAlternativeCaptcha}
						globalContactLink={transformedGlobalContactLink}
						privacyPolicyLink={transformedPrivacyPolicyLink}
						alternativeContactLink={transformedAlternativeContactLink}
					></ContactForm>
				</Drawer>
			)}
		</>
	);
};
